import { createSlice } from "@reduxjs/toolkit";
import { STATUS } from "./OrganizationSlice";
import {
  CashflowShortfallResponse,
  SupportDebugUserDetail,
  SupportDebugUserSearchResult,
  SafeToSpendResponse,
  UpcomingBillsResponse,
} from "../../services/SupportDebugService";
import {
  fetchSupportDebugCashflowAsync,
  fetchSupportDebugSafeToSpendAsync,
  fetchSupportDebugUpcomingBillsAsync,
  fetchSupportDebugUserAsync,
  searchSupportDebugUsersAsync,
  triggerSupportDebugDwRefreshAsync,
} from "../thunks/SupportDebugThunk";

export interface SupportDebugToolState<T> {
  data: T | null;
  status: string;
  error: string | null;
  // requestId of the latest dispatch. fulfilled/rejected handlers only apply
  // when they carry this exact id, so out-of-order / stale responses are
  // ignored instead of overwriting fresher data.
  currentRequestId: string | null;
}

// Cashflow additionally remembers the asOfDate the data was requested with so
// the panel's date input can re-initialize to match the data on screen after a
// tab switch (null = "today", the endpoint default).
export interface CashflowToolState extends SupportDebugToolState<CashflowShortfallResponse> {
  requestedAsOf: string | null;
}

const createToolState = <T>(): SupportDebugToolState<T> => ({
  data: null,
  status: STATUS.IDLE,
  error: null,
  currentRequestId: null,
});

const createCashflowToolState = (): CashflowToolState => ({
  ...createToolState<CashflowShortfallResponse>(),
  requestedAsOf: null,
});

// On-demand data-warehouse refresh. Page-global (not tied to the selected
// user), so it is deliberately NOT cleared by clearTools / resetSupportDebug.
// lastTriggeredAt is an ISO timestamp (kept serializable for Redux).
export interface DwRefreshState {
  status: string;
  error: string | null;
  currentRequestId: string | null;
  lastTriggeredAt: string | null;
}

const createDwRefreshState = (): DwRefreshState => ({
  status: STATUS.IDLE,
  error: null,
  currentRequestId: null,
  lastTriggeredAt: null,
});

interface SupportDebugState {
  selectedUser: SupportDebugUserDetail | null;
  userStatus: string;
  userError: string | null;
  userCurrentRequestId: string | null;
  searchResults: SupportDebugUserSearchResult[];
  searchStatus: string;
  searchError: string | null;
  searchCurrentRequestId: string | null;
  // The email the current results answer (set on pending, so responses for an
  // older query can never render under a newer one).
  searchSubmittedFor: string | null;
  // total_items from the paginated master-data response; null when the API
  // didn't return a usable total.
  searchTotalItems: number | null;
  bills: SupportDebugToolState<UpcomingBillsResponse>;
  safeToSpend: SupportDebugToolState<SafeToSpendResponse>;
  cashflow: CashflowToolState;
  dwRefresh: DwRefreshState;
}

const initialState: SupportDebugState = {
  selectedUser: null,
  userStatus: STATUS.IDLE,
  userError: null,
  userCurrentRequestId: null,
  searchResults: [],
  searchStatus: STATUS.IDLE,
  searchError: null,
  searchCurrentRequestId: null,
  searchSubmittedFor: null,
  searchTotalItems: null,
  bills: createToolState<UpcomingBillsResponse>(),
  safeToSpend: createToolState<SafeToSpendResponse>(),
  cashflow: createCashflowToolState(),
  dwRefresh: createDwRefreshState(),
};

// Reset every per-tool slice back to IDLE. Used whenever the selected user
// changes so tool data from user A never renders while user B is loading.
// Recreating the tool states also drops their currentRequestId, so any tool
// response still in flight for the previous user becomes a no-op.
const clearTools = (state: SupportDebugState) => {
  state.bills = createToolState<UpcomingBillsResponse>();
  state.safeToSpend = createToolState<SafeToSpendResponse>();
  state.cashflow = createCashflowToolState();
};

const clearSearch = (state: SupportDebugState) => {
  state.searchResults = [];
  state.searchStatus = STATUS.IDLE;
  state.searchError = null;
  state.searchCurrentRequestId = null;
  state.searchSubmittedFor = null;
  state.searchTotalItems = null;
};

const supportDebugSlice = createSlice({
  name: "supportDebug",
  initialState,
  reducers: {
    // Returning initialState also nulls every stored request id, so anything
    // still in flight when the page unmounts resolves as a no-op (no ghost
    // selected user reappearing after reset). The DW refresh state is the one
    // exception: it is not user-scoped, so an in-flight refresh keeps its id
    // (and the button stays disabled) and the last-triggered time survives
    // "Change user" and navigating away and back.
    resetSupportDebug: (state) => ({ ...initialState, dwRefresh: state.dwRefresh }),
    clearDwRefreshError: (state) => {
      state.dwRefresh.error = null;
    },
    clearToolData: (state) => {
      clearTools(state);
    },
  },
  extraReducers: (builder) => {
    builder
      // --- user search (email) ---
      .addCase(searchSupportDebugUsersAsync.pending, (state, action) => {
        state.searchStatus = STATUS.LOADING;
        state.searchError = null;
        state.searchResults = [];
        state.searchTotalItems = null;
        state.searchSubmittedFor = action.meta.arg;
        state.searchCurrentRequestId = action.meta.requestId;
      })
      .addCase(searchSupportDebugUsersAsync.fulfilled, (state, action) => {
        if (
          action.meta.requestId !== state.searchCurrentRequestId ||
          action.meta.arg !== state.searchSubmittedFor
        ) {
          return; // stale response for an older query
        }
        state.searchCurrentRequestId = null;
        state.searchStatus = STATUS.SUCCESS;
        state.searchResults = action.payload?.items ?? [];
        state.searchTotalItems = action.payload?.total_items ?? null;
      })
      .addCase(searchSupportDebugUsersAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.searchCurrentRequestId) return;
        state.searchCurrentRequestId = null;
        state.searchStatus = STATUS.FAILED;
        state.searchError = (action.payload as string) ?? "An unexpected error occurred";
        state.searchResults = [];
        state.searchTotalItems = null;
      })

      // --- selected user detail ---
      .addCase(fetchSupportDebugUserAsync.pending, (state, action) => {
        state.userStatus = STATUS.LOADING;
        state.userError = null;
        state.userCurrentRequestId = action.meta.requestId;
        // A different user may be loading: drop the previous user and their
        // tool data so nothing stale renders in the meantime.
        state.selectedUser = null;
        clearTools(state);
      })
      .addCase(fetchSupportDebugUserAsync.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.userCurrentRequestId) return; // stale response
        state.userCurrentRequestId = null;
        state.userStatus = STATUS.SUCCESS;
        state.selectedUser = action.payload ?? null;
        // The lookup completed: only now retire the search results. A failed
        // detail load keeps them visible so the admin can retry from the list.
        clearSearch(state);
      })
      .addCase(fetchSupportDebugUserAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.userCurrentRequestId) return; // stale response
        state.userCurrentRequestId = null;
        state.userStatus = STATUS.FAILED;
        state.userError = (action.payload as string) ?? "An unexpected error occurred";
        state.selectedUser = null;
      })

      // --- upcoming bills tool ---
      .addCase(fetchSupportDebugUpcomingBillsAsync.pending, (state, action) => {
        state.bills.status = STATUS.LOADING;
        state.bills.error = null;
        state.bills.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchSupportDebugUpcomingBillsAsync.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.bills.currentRequestId) return; // stale response
        state.bills.currentRequestId = null;
        // Never render tool data under a different user than it was fetched for.
        if (action.meta.arg.userId !== state.selectedUser?.id) return;
        state.bills.status = STATUS.SUCCESS;
        state.bills.data = action.payload ?? null;
      })
      .addCase(fetchSupportDebugUpcomingBillsAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.bills.currentRequestId) return; // stale response
        state.bills.currentRequestId = null;
        state.bills.status = STATUS.FAILED;
        state.bills.error = (action.payload as string) ?? "An unexpected error occurred";
        state.bills.data = null;
      })

      // --- safe to spend tool ---
      .addCase(fetchSupportDebugSafeToSpendAsync.pending, (state, action) => {
        state.safeToSpend.status = STATUS.LOADING;
        state.safeToSpend.error = null;
        state.safeToSpend.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchSupportDebugSafeToSpendAsync.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.safeToSpend.currentRequestId) return; // stale response
        state.safeToSpend.currentRequestId = null;
        // Never render tool data under a different user than it was fetched for.
        if (action.meta.arg.userId !== state.selectedUser?.id) return;
        state.safeToSpend.status = STATUS.SUCCESS;
        state.safeToSpend.data = action.payload ?? null;
      })
      .addCase(fetchSupportDebugSafeToSpendAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.safeToSpend.currentRequestId) return; // stale response
        state.safeToSpend.currentRequestId = null;
        state.safeToSpend.status = STATUS.FAILED;
        state.safeToSpend.error = (action.payload as string) ?? "An unexpected error occurred";
        state.safeToSpend.data = null;
      })

      // --- cashflow shortfall tool ---
      .addCase(fetchSupportDebugCashflowAsync.pending, (state, action) => {
        state.cashflow.status = STATUS.LOADING;
        state.cashflow.error = null;
        state.cashflow.currentRequestId = action.meta.requestId;
        state.cashflow.requestedAsOf = action.meta.arg.asOfDate ?? null;
      })
      .addCase(fetchSupportDebugCashflowAsync.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.cashflow.currentRequestId) return; // stale response
        state.cashflow.currentRequestId = null;
        // Never render tool data under a different user than it was fetched for.
        if (action.meta.arg.userId !== state.selectedUser?.id) return;
        state.cashflow.status = STATUS.SUCCESS;
        state.cashflow.data = action.payload ?? null;
      })
      .addCase(fetchSupportDebugCashflowAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.cashflow.currentRequestId) return; // stale response
        state.cashflow.currentRequestId = null;
        state.cashflow.status = STATUS.FAILED;
        state.cashflow.error = (action.payload as string) ?? "An unexpected error occurred";
        state.cashflow.data = null;
      })

      // --- data-warehouse refresh (page-global) ---
      .addCase(triggerSupportDebugDwRefreshAsync.pending, (state, action) => {
        state.dwRefresh.status = STATUS.LOADING;
        state.dwRefresh.error = null;
        state.dwRefresh.currentRequestId = action.meta.requestId;
      })
      .addCase(triggerSupportDebugDwRefreshAsync.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.dwRefresh.currentRequestId) return; // stale response
        state.dwRefresh.currentRequestId = null;
        state.dwRefresh.status = STATUS.SUCCESS;
        state.dwRefresh.lastTriggeredAt = action.payload.triggeredAt;
      })
      .addCase(triggerSupportDebugDwRefreshAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.dwRefresh.currentRequestId) return; // stale response
        state.dwRefresh.currentRequestId = null;
        state.dwRefresh.status = STATUS.FAILED;
        state.dwRefresh.error = (action.payload as string) ?? "An unexpected error occurred";
      });
  },
});

export const { resetSupportDebug, clearToolData, clearDwRefreshError } =
  supportDebugSlice.actions;
export default supportDebugSlice.reducer;
